import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { InvoiceService } from '../../utils/invoiceService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UploadZone from './components/UploadZone';
import ProcessingModeToggle from './components/ProcessingModeToggle';
import UploadQueue from './components/UploadQueue';
import BatchControls from './components/BatchControls';
import UploadHistory from './components/UploadHistory';

const UploadsPage = () => {
  const navigate = useNavigate();
  const { user, currentOrgId } = useAuth();
  const [processingMode, setProcessingMode] = useState('multi-file');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [queueStatus, setQueueStatus] = useState('idle'); // idle, processing, paused, completed
  const [workerCount, setWorkerCount] = useState(2);
  const [apiProvider, setApiProvider] = useState('gemini');
  const [geminiModel, setGeminiModel] = useState('gemini-2.0-flash');
  const [processingStats, setProcessingStats] = useState({
    totalFiles: 0,
    processedFiles: 0,
    failedFiles: 0,
    avgConfidence: 0
  });

  // Process files with AI
  useEffect(() => {
    if (queueStatus === 'processing') {
      const processFiles = async () => {
        const queuedFiles = uploadQueue.filter(f => f.status === 'queued');
        const processingFiles = uploadQueue.filter(f => f.status === 'processing');

        // Check if we can start processing more files based on worker count
        const availableWorkers = workerCount - processingFiles.length;
        const filesToProcess = queuedFiles.slice(0, availableWorkers);

        if (filesToProcess.length === 0 && processingFiles.length === 0) {
          // All files processed
          setQueueStatus('completed');
          return;
        }

        // Start processing new files
        for (const file of filesToProcess) {
          processFile(file);
        }
      };

      const processFile = async (file) => {
        const startTime = Date.now();
        const modelForRun = geminiModel;

        try {
          if (!user?.id) {
            throw new Error('User not authenticated');
          }

          // Update status to processing
          setUploadQueue(prev => prev.map(f =>
            f.id === file.id ? { ...f, status: 'processing', progress: 10 } : f
          ));

          // Step 1: Upload document to Supabase storage (30% progress)
          console.log('Uploading document to storage:', file.name);
          const document = await InvoiceService.uploadDocument(file.file, user.id, currentOrgId);

          setUploadQueue(prev => prev.map(f =>
            f.id === file.id ? { ...f, progress: 30 } : f
          ));

          // Step 2: Parse with Gemini AI (60% progress)
          console.log('Parsing invoice with AI:', file.name);
          const invoice = await InvoiceService.parseInvoice(document.id, user.id, currentOrgId, null, { model: modelForRun });

          setUploadQueue(prev => prev.map(f =>
            f.id === file.id ? { ...f, progress: 90 } : f
          ));

          // Step 3: Complete
          const processingTime = Math.floor((Date.now() - startTime) / 1000);

          setUploadQueue(prev => prev.map(f =>
            f.id === file.id ? {
              ...f,
              status: 'completed',
              progress: 100,
              confidence: invoice.confidence || 0,
              processingTime,
              invoiceCount: 1,
              modelUsed: modelForRun,
              invoiceId: invoice.id
            } : f
          ));

          console.log('Invoice processing completed:', file.name);

        } catch (error) {
          console.error('Error processing file:', error);
          setUploadQueue(prev => prev.map(f =>
            f.id === file.id ? {
              ...f,
              status: 'error',
              progress: 100,
              error: error.message || 'Failed to process file'
            } : f
          ));
        }
      };

      processFiles();
    }
  }, [queueStatus, uploadQueue, workerCount, geminiModel]);

  // Update processing stats
  useEffect(() => {
    const totalFiles = uploadQueue?.length;
    const processedFiles = uploadQueue?.filter(f => f?.status === 'completed')?.length;
    const failedFiles = uploadQueue?.filter(f => f?.status === 'error')?.length;
    const completedFiles = uploadQueue?.filter(f => f?.status === 'completed' && f?.confidence);
    const avgConfidence = completedFiles?.length > 0
      ? completedFiles?.reduce((sum, f) => sum + f?.confidence, 0) / completedFiles?.length
      : 0;

    setProcessingStats({
      totalFiles,
      processedFiles,
      failedFiles,
      avgConfidence
    });
  }, [uploadQueue]);

  // Auto-navigate to invoice editor when processing completes
  useEffect(() => {
    if (queueStatus === 'completed' && uploadQueue.length > 0) {
      const completedFiles = uploadQueue.filter(f => f.status === 'completed' && f.invoiceId);
      if (completedFiles.length === 1) {
        // Single file - navigate directly to invoice editor
        navigate(`/invoice-editor?id=${completedFiles[0].invoiceId}`);
      } else if (completedFiles.length > 1) {
        // Multiple files - navigate to invoices list
        navigate('/invoices');
      }
    }
  }, [queueStatus, uploadQueue, navigate]);

  const handleFilesSelected = (files) => {
    const newFiles = files?.map((file, index) => ({
      id: `file_${Date.now()}_${index}`,
      name: file?.name,
      size: file?.size,
      type: file?.type,
      status: 'queued',
      progress: 0,
      confidence: null,
      processingTime: null,
      invoiceCount: null,
      error: null,
      file: file
    }));

    setUploadQueue(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (fileId) => {
    setUploadQueue(prev => prev?.filter(f => f?.id !== fileId));
  };

  const handleRetryFile = (fileId) => {
    setUploadQueue(prev => prev?.map(f => 
      f?.id === fileId 
        ? { ...f, status: 'queued', progress: 0, error: null, confidence: null, modelUsed: null }
        : f
    ));

    setQueueStatus(prevStatus => prevStatus === 'processing' ? prevStatus : 'processing');
  };

  const handleClearQueue = () => {
    if (queueStatus !== 'processing') {
      setUploadQueue([]);
      setQueueStatus('idle');
    }
  };

  const handleStartProcessing = () => {
    if (uploadQueue?.length > 0) {
      setQueueStatus('processing');
    }
  };

  const handlePauseProcessing = () => {
    setQueueStatus('paused');
  };

  const handleResumeProcessing = () => {
    setQueueStatus('processing');
  };

  const handleStopProcessing = () => {
    setQueueStatus('idle');
    setUploadQueue(prev => prev?.map(f => 
      f?.status === 'processing' || f?.status === 'queued'
        ? { ...f, status: 'queued', progress: 0 }
        : f
    ));
  };

  const handleViewInvoices = () => {
    navigate('/invoices');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Upload" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">File Uploads</h1>
                <p className="text-sm text-muted-foreground">
                  Batch process invoice files with AI-powered OCR extraction
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {processingStats?.totalFiles > 0 && (
                <div className="flex items-center space-x-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-foreground">{processingStats?.totalFiles}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-success">{processingStats?.processedFiles}</div>
                    <div className="text-xs text-muted-foreground">Processed</div>
                  </div>
                  {processingStats?.failedFiles > 0 && (
                    <div className="text-center">
                      <div className="font-semibold text-error">{processingStats?.failedFiles}</div>
                      <div className="text-xs text-muted-foreground">Failed</div>
                    </div>
                  )}
                  {processingStats?.avgConfidence > 0 && (
                    <div className="text-center">
                      <div className="font-semibold text-primary">
                        {Math.round(processingStats?.avgConfidence * 100)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Confidence</div>
                    </div>
                  )}
                </div>
              )}

              <Button
                variant="outline"
                onClick={handleViewInvoices}
                iconName="FileText"
                iconPosition="left"
              >
                View Invoices
              </Button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Upload & Controls */}
            <div className="lg:col-span-2 space-y-6">
              {/* Processing Mode Toggle */}
              <ProcessingModeToggle
                mode={processingMode}
                onModeChange={setProcessingMode}
                disabled={queueStatus === 'processing'}
              />

              {/* Upload Zone */}
              <UploadZone
                onFilesSelected={handleFilesSelected}
                isProcessing={queueStatus === 'processing'}
              />

              {/* Batch Controls */}
              <BatchControls
                queueStatus={queueStatus}
                onStart={handleStartProcessing}
                onPause={handlePauseProcessing}
                onResume={handleResumeProcessing}
                onStop={handleStopProcessing}
                workerCount={workerCount}
                onWorkerCountChange={setWorkerCount}
                apiProvider={apiProvider}
                onApiProviderChange={setApiProvider}
                geminiModel={geminiModel}
                onGeminiModelChange={setGeminiModel}
                hasFiles={uploadQueue?.length > 0}
              />

              {/* Upload Queue */}
              <UploadQueue
                files={uploadQueue}
                onRemoveFile={handleRemoveFile}
                onRetryFile={handleRetryFile}
                onClearQueue={handleClearQueue}
                isProcessing={queueStatus === 'processing'}
              />
            </div>

            {/* Right Column - History & Info */}
            <div className="space-y-6">
              {/* Upload History */}
              <UploadHistory />

              {/* Processing Tips */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Lightbulb" size={18} className="text-warning" />
                  <h3 className="text-sm font-medium text-foreground">Processing Tips</h3>
                </div>

                <div className="space-y-3 text-xs text-muted-foreground">
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={12} className="text-success mt-0.5 flex-shrink-0" />
                    <p>Use high-resolution scans (300+ DPI) for better OCR accuracy</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={12} className="text-success mt-0.5 flex-shrink-0" />
                    <p>Ensure invoice text is clearly visible and not skewed</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={12} className="text-success mt-0.5 flex-shrink-0" />
                    <p>PDF files generally provide better extraction results than images</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={12} className="text-success mt-0.5 flex-shrink-0" />
                    <p>Use 2-3 workers for optimal processing speed vs API limits</p>
                  </div>
                </div>
              </div>

              {/* File Requirements */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="FileCheck" size={18} className="text-primary" />
                  <h3 className="text-sm font-medium text-foreground">File Requirements</h3>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Supported formats:</span>
                    <span className="text-foreground font-medium">PDF, JPG, PNG</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Maximum file size:</span>
                    <span className="text-foreground font-medium">50 MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Batch limit:</span>
                    <span className="text-foreground font-medium">50 files</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processing time:</span>
                    <span className="text-foreground font-medium">~2-5 min/file</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UploadsPage;
